import apiClient from '@/lib/apiClient';
import type { EmailPreviewResult, SendEmailResult } from '@/services/email.service';
import { ApiResponse } from '@/types';
import type {
  RenewalCancellationReasonValue,
  RenewalFrequencyValue,
  RenewalStatusValue,
} from '@/utils/constants';

// ─── DTOs (mirror server/src/services/renewal.service.ts) ────────────────────
// Due-date window filters ('overdue' and 'missed' are server-side aliases;
// 'custom' reads the separate from/to params).
export type RenewalPeriodFilter =
  'all' | 'today' | 'thisWeek' | 'thisMonth' | 'nextMonth' | 'overdue' | 'missed' | 'custom';

/** One dated note explaining a due-date move (ADR-054). */
export interface DueDateNote {
  /**
   * The date the renewal was moved FROM. Absent on notes written before this
   * was recorded, and equal to `dueDate` when the biller left a note without
   * moving anything - both render as a bare date rather than an arrow.
   */
  fromDueDate?: string | null;
  dueDate: string;
  note: string;
  at: string;
  byId: number | null;
}

export type RenewalSortKey = 'dueDate' | 'createdAt' | 'amount' | 'clientName' | 'status';

export type RenewalReminderKind = 'notice' | 'followup1' | 'followup2' | 'termination';

export interface RenewalEmailFlags {
  noticeSent: boolean;
  followup1Sent: boolean;
  followup2Sent: boolean;
  terminationSent: boolean;
}

export interface RenewalListItem {
  id: number;
  renewalCode: string;
  leadId: number;
  clientName: string;
  arn: string;
  productCode: string;
  productName: string;
  amount: number | null;
  annualAmount: number | null;
  frequency: RenewalFrequencyValue;
  dueDate: string;
  status: RenewalStatusValue;
  cycleNumber: number;
  emailFlags: RenewalEmailFlags;
  remindersSentCount: number;
  paid: boolean;
  paidAt: string | null;
  completedAt: string | null;
  cancelledAt: string | null;
  /** Skip chosen at step 1 for a Non-ARN client (ADKJ-117). */
  skippedAt: string | null;
  createdAt: string;
  /** The "date -> note" log behind the queue's Notes button (ADR-054). */
  dueDateNotes: DueDateNote[];
  /** Non-null only while the subscription is actually switched off. */
  deactivatedAt: string | null;
  deactivationReason: RenewalCancellationReasonValue | null;
  deactivationNote: string | null;
}

export interface RenewalMetrics {
  dueThisMonth: number;
  dueNextMonth: number;
  overdue: number;
  // Sum of the per-period amounts of verified-but-unpaid, non-terminated rows.
  paymentPendingAmount: number;
  // Renewals completed in the current calendar month.
  renewedMtd: number;
  // Renewals terminated/lost in the current calendar month.
  cancelledMtd: number;
}

export interface RenewalStatusCount {
  status: RenewalStatusValue;
  count: number;
}

export interface RenewalMonthlyRow {
  month: string;
  due: number;
  renewed: number;
  // Σ invoice_amount of renewals completed in the month.
  collected: number;
  // Renewals due in the month that are not yet complete/terminated.
  pending: number;
}

export interface RenewalByProductRow {
  productCode: string;
  productName: string;
  pendingCount: number;
  renewedCount: number;
  cancelledCount: number;
}

export interface RenewalUpcomingRow {
  id: number;
  renewalCode: string;
  clientName: string;
  arn: string;
  productName: string;
  amount: number | null;
  dueDate: string;
  status: RenewalStatusValue;
}

export interface RenewalDashboard {
  statusFunnel: RenewalStatusCount[];
  monthly: RenewalMonthlyRow[];
  byProduct: RenewalByProductRow[];
  upcoming: RenewalUpcomingRow[];
}

export interface RenewalDashboardParams {
  productCode?: string;
  year?: number;
}

export interface RenewalCalendarItem {
  id: number;
  renewalCode: string;
  clientName: string;
  arn: string;
  productName: string;
  amount: number | null;
  dueDate: string;
  status: RenewalStatusValue;
}

export interface RenewalPricing {
  productCode: string;
  productName: string;
  isAumBased: boolean;
  // Combined primary + additional-ARN AUM (crores) used for the slab lookup.
  combinedAum: number | null;
  slabBand: string | null;
  annualAmount: number;
  // Per-period figure billed this cycle: round(annual / divisor(frequency)).
  // EXCLUDES GST - totalAmount is what the client is asked to pay.
  amount: number;
  frequency: RenewalFrequencyValue;
  customAmount: number | null;
  // Combined AUM > 1000 Cr needs approval - flagged, not blocked (v1).
  approvalRequired: boolean;
  // Biller discount on this cycle. `grossAmount` is the per-period figure
  // before it; `amount` above is already net of it and is what GST applies to.
  discountMode: 'amount' | 'percent' | null;
  discountValue: number | null;
  discountAmount: number;
  grossAmount: number;
  // Tax split on `amount`; totalAmount (GST-inclusive) is the invoice default
  // and matches the Total Payable in the renewal emails.
  gstPercentage: number;
  gstAmount: number;
  totalAmount: number;
}

export interface RenewalAdditionalArnRow {
  id: number;
  arn: string;
  clientName: string;
  aum: number | null;
}

export interface RenewalClientBlock {
  clientName: string;
  businessName: string;
  arn: string;
  /** Non-ARN client (ADKJ-117): `arn` is a system code and Skip is offered. */
  isNonArn: boolean;
  arnAum: number | null;
  /** When the AUM above was last fetched live from AMFI; null = never fetched. */
  aumFetchedAt: string | null;
  gstin: string | null;
  legalName: string | null;
  displayName: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  pincode: string | null;
  email: string;
  mobile: string;
  primaryEmail: string | null;
  primaryNumber: string | null;
  secondaryEmail: string | null;
  secondaryNumber: string | null;
  rmName: string;
  rmEmail: string;
  additionalArns: RenewalAdditionalArnRow[];
}

export interface RenewalOwnedProduct {
  productCode: string;
  productName: string;
  termYears: number | null;
  customPrice: number | null;
}

export interface RenewalReminderRow {
  id: number;
  kind: RenewalReminderKind;
  scheduledFor: string;
  status: string;
  sentAt: string | null;
}

export interface RenewalEmailStamps {
  noticeSentAt: string | null;
  followup1SentAt: string | null;
  followup2SentAt: string | null;
  terminationSentAt: string | null;
}

export interface RenewalDetail {
  id: number;
  renewalCode: string;
  leadId: number;
  productCode: string;
  cycleNumber: number;
  status: RenewalStatusValue;
  frequency: RenewalFrequencyValue;
  dueDate: string;
  dueDateChangedAt: string | null;
  dueDateChangedByName: string | null;
  // True once the due date has passed - the biller may send the manual service
  // termination (it is no longer part of the automated ladder).
  canSendTermination: boolean;
  // Guidance only: due date + the admin-configured grace days.
  terminationRecommendedFrom: string;
  // Step 1's own completion stamp (saving the client block), separate from
  // verifiedAt so step 1 can read complete before step 2 is done.
  clientDetailsConfirmedAt: string | null;
  clientDetailsConfirmedByName: string | null;
  termYears: number;
  serviceEnabledAt: string;
  client: RenewalClientBlock;
  products: RenewalOwnedProduct[];
  pricing: RenewalPricing;
  /** Set while the live-fetched AUM is 0 and the automated reminders are held. */
  zeroAumHoldAt: string | null;
  emailSchedule: RenewalReminderRow[];
  emailStamps: RenewalEmailStamps;
  verifiedAt: string | null;
  verifiedByName: string | null;
  verifyNotes: string | null;
  payment: {
    paidAt: string | null;
    transactionNo: string | null;
    mode: string | null;
    recordedByName: string | null;
  };
  invoice: {
    invoiceNumber: string | null;
    invoiceDate: string | null;
    invoiceAmount: number | null;
    invoicePdfName: string | null;
    hasInvoicePdf: boolean;
    invoiceRecordedAt: string | null;
    invoiceRecordedByName: string | null;
  };
  serviceNotification: {
    serviceNotifiedAt: string | null;
    serviceNotifiedByName: string | null;
    serviceQueueRef: string | null;
  };
  clientInvoice: {
    sentAt: string | null;
    sentByName: string | null;
  };
  completion: {
    completedAt: string | null;
    completedByName: string | null;
  };
  cancellation: {
    cancelledAt: string | null;
    reason: RenewalCancellationReasonValue | null;
    note: string | null;
    cancelledByName: string | null;
  };
  /** Renewal skipped at step 1 - offered to Non-ARN clients only (ADKJ-117). */
  skip: {
    skippedAt: string | null;
    skippedByName: string | null;
    canSkip: boolean;
  };
  /** Service switched off, and the two notices required to switch it off. */
  deactivation: {
    reason: RenewalCancellationReasonValue | null;
    note: string | null;
    clientSentAt: string | null;
    serviceSentAt: string | null;
    deactivatedAt: string | null;
    deactivatedByName: string | null;
    reactivatedAt: string | null;
    reactivatedByName: string | null;
    /** Plain-English working of the recalculated due date, shown on hover. */
    reactivationBasis: string | null;
    canDeactivate: boolean;
    canReactivate: boolean;
    /** True when a started-but-not-yet-effective deactivation can be called off. */
    canAbort: boolean;
  };
  dueDateNotes: DueDateNote[];
}

/**
 * Result of a live AUM fetch (AMFI ARN master): 'refreshed' = saved and the
 * renewal re-priced; 'unavailable' = the API had no answer, stored AUM kept;
 * 'not_applicable' = not an AUM-priced renewal.
 */
export type AumRefreshOutcome = 'refreshed' | 'unavailable' | 'not_applicable';

export interface RenewalAumRefreshResult {
  outcome: AumRefreshOutcome;
  detail: RenewalDetail;
}

/** One row of Renewals > Zero AUM: reminders held because the live AUM is 0. */
export interface ZeroAumHoldItem {
  id: number;
  renewalCode: string;
  leadId: number;
  clientName: string;
  arn: string;
  productName: string;
  dueDate: string;
  status: RenewalStatusValue;
  heldSince: string;
  aumFetchedAt: string | null;
  pendingReminders: number;
  nextReminderKind: RenewalReminderKind;
  /** When the held reminder is next re-checked against the live AUM. */
  nextReminderAt: string;
}

export interface DeactivateSubscriptionPayload {
  reason: RenewalCancellationReasonValue;
  /** Mandatory when the reason is 'Other'. */
  note?: string;
}

/** Which of the two deactivation notices is being previewed or sent. */
export type DeactivationAudience = 'client' | 'service';

export interface CancelSubscriptionPayload {
  reason: RenewalCancellationReasonValue;
  // Mandatory when the reason is 'Other'.
  note?: string;
}

export interface CompleteRenewalResult {
  detail: RenewalDetail;
  nextCycle: { id: number; cycleNumber: number; dueDate: string } | null;
}

// ─── Request payloads (mirror server renewal.validators.ts) ──────────────────
export interface RenewalClientDetailsPayload {
  primaryEmail?: string;
  primaryNumber?: string;
  secondaryNumber?: string;
  secondaryEmail?: string;
  gstin?: string;
  legalName?: string;
  displayName?: string;
  address?: string;
  city?: string;
  state?: string;
  pincode?: string;
  arnAum?: string;
  additionalArns?: { id: number; aum: number }[];
}

export interface RenewalPricingPatchPayload {
  frequency?: RenewalFrequencyValue;
  // Biller override for non-AUM products, whole rupees; null clears it.
  customAmount?: number | null;
  // Per-renewal discount; null on either field clears it. Unlike the fields
  // above this stays editable after verification, until a payment is recorded.
  discountMode?: 'amount' | 'percent' | null;
  discountValue?: number | null;
}

export interface RecordRenewalPaymentPayload {
  transactionNo: string;
  mode: string;
}

export interface RecordRenewalInvoicePayload {
  invoiceNumber: string;
  invoiceDate: string;
  invoiceAmount: number;
  pdfBase64: string;
  pdfName: string;
}

export interface SendRenewalInvoicePayload {
  templateSlug: string;
  to: string[];
  cc?: string[];
  bcc?: string[];
  subject: string;
  bodyHtml: string;
  bodyText: string;
  fromEmail?: string;
}

// Internal notice to the Service team. The template slug is pinned server-side,
// so it is deliberately absent here.
export type SendInternalNoticePayload = Omit<SendRenewalInvoicePayload, 'templateSlug'>;

export type RenewalSendResult = { detail: RenewalDetail; email: SendEmailResult };

export interface RenewalListParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: RenewalStatusValue;
  period?: RenewalPeriodFilter;
  /** Inclusive due-date bounds, read only when `period` is 'custom'. */
  from?: string;
  to?: string;
  productCode?: string;
  sortBy?: RenewalSortKey;
  sortDir?: 'asc' | 'desc';
  /**
   * Restricts the result to deactivated subscriptions - what the Deactivated
   * tab asks for. Overrides `status`, and never overlaps the Pipeline.
   */
  onlyDeactivated?: boolean;
  /**
   * Drops finished subscriptions from the result. The Pipeline is a work list,
   * so a plan the client has already renewed, cancelled or upgraded away from
   * does not belong in it; History asks for those statuses by name instead.
   */
  excludeTerminal?: boolean;
}

export interface RenewalListResponse {
  rows: RenewalListItem[];
  total: number;
  totalPages: number;
  currentPage: number;
}

const toParams = (params: RenewalListParams): Record<string, string | number> => {
  const out: Record<string, string | number> = {};
  if (params.page) out.page = params.page;
  if (params.limit) out.limit = params.limit;
  if (params.search) out.search = params.search;
  if (params.status) out.status = params.status;
  if (params.period && params.period !== 'all') out.period = params.period;
  if (params.period === 'custom') {
    if (params.from) out.from = params.from;
    if (params.to) out.to = params.to;
  }
  if (params.productCode) out.productCode = params.productCode;
  if (params.sortBy) out.sortBy = params.sortBy;
  if (params.sortDir) out.sortDir = params.sortDir;
  if (params.excludeTerminal) out.excludeTerminal = 'true';
  if (params.onlyDeactivated) out.onlyDeactivated = 'true';
  return out;
};

export const renewalsService = {
  list: async (params: RenewalListParams = {}): Promise<RenewalListResponse> => {
    const { data } = await apiClient.get<ApiResponse<RenewalListItem[]>>('/renewals', {
      params: toParams(params),
    });
    return {
      rows: data.data ?? [],
      total: data.meta?.total ?? 0,
      totalPages: data.meta?.totalPages ?? 1,
      currentPage: data.meta?.currentPage ?? 1,
    };
  },

  metrics: async (): Promise<RenewalMetrics> => {
    const { data } = await apiClient.get<ApiResponse<RenewalMetrics>>('/renewals/metrics');
    if (!data.data) throw new Error('Renewal metrics not found in response');
    return data.data;
  },

  dashboard: async (params: RenewalDashboardParams = {}): Promise<RenewalDashboard> => {
    const query: Record<string, string | number> = {};
    if (params.productCode) query.productCode = params.productCode;
    if (params.year) query.year = params.year;
    const { data } = await apiClient.get<ApiResponse<RenewalDashboard>>('/renewals/dashboard', {
      params: query,
    });
    if (!data.data) throw new Error('Renewal dashboard not found in response');
    return data.data;
  },

  calendar: async (year: number, month: number): Promise<RenewalCalendarItem[]> => {
    const { data } = await apiClient.get<ApiResponse<RenewalCalendarItem[]>>('/renewals/calendar', {
      params: { year, month },
    });
    return data.data ?? [];
  },

  getById: async (id: number): Promise<RenewalDetail> => {
    const { data } = await apiClient.get<ApiResponse<RenewalDetail>>(`/renewals/${id}`);
    if (!data.data) throw new Error('Renewal not found in response');
    return data.data;
  },

  // Fetches the client's live AUM, saves it and re-prices the renewal.
  refreshAum: async (id: number): Promise<RenewalAumRefreshResult> => {
    const { data } = await apiClient.post<ApiResponse<RenewalAumRefreshResult>>(
      `/renewals/${id}/refresh-aum`,
    );
    if (!data.data) throw new Error('Renewal not found in response');
    return data.data;
  },

  zeroAumHolds: async (): Promise<ZeroAumHoldItem[]> => {
    const { data } = await apiClient.get<ApiResponse<ZeroAumHoldItem[]>>(
      '/renewals/zero-aum-holds',
    );
    return data.data ?? [];
  },

  updateClientDetails: async (
    id: number,
    payload: RenewalClientDetailsPayload,
  ): Promise<RenewalDetail> => {
    const { data } = await apiClient.put<ApiResponse<RenewalDetail>>(
      `/renewals/${id}/client-details`,
      payload,
    );
    if (!data.data) throw new Error('Renewal not found in response');
    return data.data;
  },

  updatePricing: async (
    id: number,
    payload: RenewalPricingPatchPayload,
  ): Promise<RenewalDetail> => {
    const { data } = await apiClient.patch<ApiResponse<RenewalDetail>>(
      `/renewals/${id}/pricing`,
      payload,
    );
    if (!data.data) throw new Error('Renewal not found in response');
    return data.data;
  },

  // Moves the renewal due date; every not-yet-sent ladder email is re-slotted
  // against the new date server-side. An optional note is logged against the
  // date, so the queue's Notes popup can show what was promised on each one.
  updateDueDate: async (id: number, dueDate: string, note?: string): Promise<RenewalDetail> => {
    const { data } = await apiClient.patch<ApiResponse<RenewalDetail>>(`/renewals/${id}/due-date`, {
      dueDate,
      ...(note?.trim() ? { note: note.trim() } : {}),
    });
    if (!data.data) throw new Error('Renewal not found in response');
    return data.data;
  },

  // ─── Deactivation / reactivation (ADR-054) ─────────────────────────────────
  // Records WHY the subscription is being switched off. Nothing takes effect
  // until both notices below have actually been sent.
  startDeactivation: async (
    id: number,
    payload: DeactivateSubscriptionPayload,
  ): Promise<RenewalDetail> => {
    const { data } = await apiClient.post<ApiResponse<RenewalDetail>>(
      `/renewals/${id}/deactivate`,
      { reason: payload.reason, ...(payload.note ? { note: payload.note } : {}) },
    );
    if (!data.data) throw new Error('Renewal not found in response');
    return data.data;
  },

  previewDeactivationEmail: async (
    id: number,
    audience: DeactivationAudience,
  ): Promise<EmailPreviewResult> => {
    const { data } = await apiClient.get<ApiResponse<EmailPreviewResult>>(
      `/renewals/${id}/deactivation-preview/${audience}`,
    );
    if (!data.data) throw new Error('Deactivation notice preview not found in response');
    return data.data;
  },

  // Sending the SECOND notice is what actually deactivates the subscription.
  sendDeactivationEmail: async (
    id: number,
    audience: DeactivationAudience,
    payload: SendInternalNoticePayload,
  ): Promise<RenewalSendResult> => {
    const { data } = await apiClient.post<ApiResponse<RenewalSendResult>>(
      `/renewals/${id}/deactivation-email/${audience}`,
      payload,
      { timeout: 60_000 },
    );
    if (!data.data) throw new Error('Send result not found in response');
    return data.data;
  },

  // Calls off a deactivation that has not taken effect yet - the only way back
  // out once the client notice has gone but the Service one has not.
  abortDeactivation: async (id: number): Promise<RenewalDetail> => {
    const { data } = await apiClient.post<ApiResponse<RenewalDetail>>(
      `/renewals/${id}/deactivation-abort`,
    );
    if (!data.data) throw new Error('Renewal not found in response');
    return data.data;
  },

  // Brings the client back: restarts this renewal at step 1 on a due date
  // rebuilt from the unused part of the term they had already paid for.
  reactivate: async (id: number): Promise<RenewalDetail> => {
    const { data } = await apiClient.post<ApiResponse<RenewalDetail>>(`/renewals/${id}/reactivate`);
    if (!data.data) throw new Error('Renewal not found in response');
    return data.data;
  },

  // Manually sends the service-termination pair (client closure + internal
  // Service notice) and cancels the subscription. Never automated.
  sendTermination: async (id: number): Promise<RenewalDetail> => {
    const { data } = await apiClient.post<ApiResponse<RenewalDetail>>(
      `/renewals/${id}/send-termination`,
      undefined,
      { timeout: 60_000 },
    );
    if (!data.data) throw new Error('Renewal not found in response');
    return data.data;
  },

  verify: async (id: number, notes?: string): Promise<RenewalDetail> => {
    const { data } = await apiClient.post<ApiResponse<RenewalDetail>>(`/renewals/${id}/verify`, {
      ...(notes ? { notes } : {}),
    });
    if (!data.data) throw new Error('Renewal not found in response');
    return data.data;
  },

  recordPayment: async (
    id: number,
    payload: RecordRenewalPaymentPayload,
  ): Promise<RenewalDetail> => {
    const { data } = await apiClient.post<ApiResponse<RenewalDetail>>(
      `/renewals/${id}/payment`,
      payload,
    );
    if (!data.data) throw new Error('Renewal not found in response');
    return data.data;
  },

  recordInvoice: async (
    id: number,
    payload: RecordRenewalInvoicePayload,
  ): Promise<RenewalDetail> => {
    const { data } = await apiClient.post<ApiResponse<RenewalDetail>>(
      `/renewals/${id}/invoice`,
      payload,
      { timeout: 60_000 },
    );
    if (!data.data) throw new Error('Renewal not found in response');
    return data.data;
  },

  // Preview of the internal notice telling the Service team to continue service
  // for the renewed cycle. Also mints the service queue reference server-side
  // so the preview shows the real one.
  previewServiceNotification: async (id: number): Promise<EmailPreviewResult> => {
    const { data } = await apiClient.get<ApiResponse<EmailPreviewResult>>(
      `/renewals/${id}/service-preview`,
    );
    if (!data.data) throw new Error('Service notification preview not found in response');
    return data.data;
  },

  // Emails the Service team, then stamps the notify step. The email IS the
  // notification - there is no silent "mark as notified" path.
  notifyService: async (
    id: number,
    payload: SendInternalNoticePayload,
  ): Promise<RenewalSendResult> => {
    const { data } = await apiClient.post<ApiResponse<RenewalSendResult>>(
      `/renewals/${id}/notify-service`,
      payload,
      { timeout: 60_000 },
    );
    if (!data.data) throw new Error('Send result not found in response');
    return data.data;
  },

  // Renewal-scoped preview: shows the SAME invoice number/date/total that will
  // actually be sent (built from the recorded invoice, not a live quotation).
  previewInvoiceEmail: async (id: number): Promise<EmailPreviewResult> => {
    const { data } = await apiClient.get<ApiResponse<EmailPreviewResult>>(
      `/renewals/${id}/invoice-preview`,
    );
    if (!data.data) throw new Error('Invoice email preview not found in response');
    return data.data;
  },

  // Read-only preview of one automated ladder email, rendered exactly as the
  // dispatcher will send it. Sends nothing and stamps nothing.
  previewLadderEmail: async (
    id: number,
    kind: RenewalReminderKind,
  ): Promise<EmailPreviewResult> => {
    const { data } = await apiClient.get<ApiResponse<EmailPreviewResult>>(
      `/renewals/${id}/email-preview`,
      { params: { kind } },
    );
    if (!data.data) throw new Error('Renewal email preview not found in response');
    return data.data;
  },

  sendInvoice: async (
    id: number,
    payload: SendRenewalInvoicePayload,
  ): Promise<{ detail: RenewalDetail; email: SendEmailResult }> => {
    type SendResult = { detail: RenewalDetail; email: SendEmailResult };
    const { data } = await apiClient.post<ApiResponse<SendResult>>(
      `/renewals/${id}/send-invoice`,
      payload,
      { timeout: 60_000 },
    );
    if (!data.data) throw new Error('Send result not found in response');
    return data.data;
  },

  complete: async (id: number): Promise<CompleteRenewalResult> => {
    const { data } = await apiClient.post<ApiResponse<CompleteRenewalResult>>(
      `/renewals/${id}/complete`,
    );
    if (!data.data) throw new Error('Complete result not found in response');
    return data.data;
  },

  cancelSubscription: async (
    id: number,
    payload: CancelSubscriptionPayload,
  ): Promise<RenewalDetail> => {
    const { data } = await apiClient.post<ApiResponse<RenewalDetail>>(`/renewals/${id}/cancel`, {
      reason: payload.reason,
      ...(payload.note ? { note: payload.note } : {}),
    });
    if (!data.data) throw new Error('Renewal not found in response');
    return data.data;
  },

  // Skip this cycle outright (Non-ARN clients only, ADKJ-117): closes the row
  // at step 1 - no workflow, no emails, no next cycle.
  skipRenewal: async (id: number): Promise<RenewalDetail> => {
    const { data } = await apiClient.post<ApiResponse<RenewalDetail>>(`/renewals/${id}/skip`);
    if (!data.data) throw new Error('Renewal not found in response');
    return data.data;
  },

  downloadInvoicePdf: async (id: number): Promise<void> => {
    const response = await apiClient.get<Blob>(`/renewals/${id}/invoice-pdf`, {
      responseType: 'blob',
    });
    const disposition = response.headers['content-disposition'] as string | undefined;
    const match = disposition?.match(/filename="?([^"]+)"?/);
    const filename = match?.[1] ?? `renewal-invoice-${id}.pdf`;
    const blobUrl = window.URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  },
};
